import { defineStore } from "pinia";
import { ref } from "vue";
import * as graphql from "../graphql";
import type { Region } from "../graphql";
import apolloClient from "../graphql/client";
import { useApolloErrorHandling } from "@/util/useApolloErrorHandling";

/**
 * Pending mod issues for the Valheim mods tab (TR-5319).
 *
 * ⚠️ A store rather than a few lines in `game_profiles/valheim.ts`. The profile modules are pulled in
 * while the app boots, and importing the Apollo client into one closes a cycle through
 * `graphql/client` — the panel rendered a blank page with no error beyond a pile of failed module
 * fetches. Stores are already on the far side of that boundary (`store/steam.ts` does the same), so
 * this is where a query belongs.
 */
interface PendingIssues {
  missing: Array<{
    repoKey: string;
    name: string;
    fullName: string;
    requiredBy: string[];
    requiredByKeys: string[];
    containedInPack: boolean;
  }>;
  /** Server-relevant packages our catalogue does not carry — worth a warning. */
  unavailable: string[];
  /** Mods that run in the player's game. Stated as a fact, never as a problem. */
  clientOnly: string[];
  /** Could not be classified, so the panel falls back to hedged wording. */
  unresolved: string[];
}

export const useValheimModsStore = defineStore("valheimMods", () => {
  const { handleApolloErrorDefault } = useApolloErrorHandling();

  /**
   * What is wrong with the selection as it stands right now, including mods added since the last save.
   *
   * The panel model is computed on a config *read*, so a mod picked a moment ago is not in it. Project
   * Zomboid solves this the same way, with `projectZomboidPendingIssues`.
   */
  const pending = ref<PendingIssues>({ missing: [], unavailable: [], clientOnly: [], unresolved: [] });

  function clearPending() {
    pending.value = { missing: [], unavailable: [], clientOnly: [], unresolved: [] };
  }

  /**
   * Ask the backend what the current, unsaved selection is missing.
   *
   * Backed by the same `resolve_selection` the save path uses, so the notices cannot describe a
   * different selection from the one that gets installed. Replaces the whole list rather than merging:
   * this is the answer for the selection as it stands, and a dependency the customer has just added is
   * no longer missing.
   */
  async function refreshPending(sid: number, region: Region, repoKeys: string[]): Promise<PendingIssues> {
    try {
      const result = await apolloClient.query({
        query: graphql.ValheimPendingModIssuesDocument,
        variables: { sid, region, repoKeys },
        // The catalogue moves under us — an author adds a dependency in a new version — and a cached
        // answer would describe yesterday's requirements.
        fetchPolicy: "network-only",
      });
      const resolved = result.data?.valheimPendingModIssues;
      if (resolved) {
        pending.value = {
          missing: (resolved.missing ?? []).map((entry) => ({
            repoKey: entry.repoKey,
            name: entry.name,
            fullName: entry.fullName,
            requiredBy: (entry.requiredBy ?? []).filter((name): name is string => !!name),
            requiredByKeys: (entry.requiredByKeys ?? []).filter((key): key is string => !!key),
            containedInPack: entry.containedInPack,
          })),
          unavailable: resolved.unavailable ?? [],
          clientOnly: resolved.clientOnly ?? [],
          unresolved: resolved.unresolved ?? [],
        };
      }
    } catch (error) {
      // The notices are advisory; the config read produces the same ones after a save. Losing them
      // must not break the click that triggered this.
      handleApolloErrorDefault(error);
    }
    return pending.value;
  }

  return { pending, clearPending, refreshPending };
});
